import React, { useMemo, useRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { initialize } from 'launchdarkly-js-client-sdk';
import { v4 as getUUID } from 'uuid';
import {
  getOrCreateGlobalContext,
  CONTEXT_NAMES
} from '@adsk/bim360-matrix-react-helpers/es/globalContexts';

const anonymousUser = { key: getUUID(), anonymous: true };

/**
 * This method creates a new LaunchDarkly context, consumer & provider.
 * We need this method in order to have multiple contexts in a single page (one for Matrix core, one for FM, etc.)
 * That's the reason for creating `LaunchDarklyContext` inside this method, instead of globally.
 *
 * @param {{ context }} - A context to use for the provider. Useful when need to verify we only have one global context.
 * @param {{ defaultFlagsValues }} - A key-value object of flagNames and their default values (e.g: {[myFlagName]: defaultValue}).
 * @returns {{ useFeatureFlag, LaunchDarklyProvider, LaunchDarklyConsumer, LaunchDarklyContext}}
 */
const createLaunchDarklyContext = ({ context, defaultFlagsValues = {}} = {}) => {
  const LaunchDarklyContext = context || React.createContext();

  const LaunchDarklyProvider = ({ apiKey, user, children }) => {
    const client = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
      if (user) {
        const ldUser = { ...anonymousUser, ...user };
        if (client.current === null) {
          client.current = initialize(apiKey, ldUser, {
            allAttributesPrivate: true
          });
          client.current.on('ready', () => {
            setIsLoading(false);
          });
        } else {
          setIsLoading(true);
          client.current.identify(ldUser, null, () => setIsLoading(false));
        }
      }
    }, [apiKey, user]);

    const memoizedValue = useMemo(() => ({ isLoading, client }), [isLoading, client]);
    return (
      <LaunchDarklyContext.Provider value={memoizedValue}>{children}</LaunchDarklyContext.Provider>
    );
  };

  LaunchDarklyProvider.defaultProps = {
    user: {}
  };

  LaunchDarklyProvider.propTypes = {
    /**
     * LaunchDarkly API key
     */
    apiKey: PropTypes.string.isRequired,
    /**
     * LaunchDarkly user - used when need to identify specific users (by region, project ID etc.)
     */
    user: PropTypes.object,
    /**
     * Children
     */
    children: PropTypes.node.isRequired
  };

  /**
   *
   * @param flagName
   * @param defaultValue - An optional default value in case launchDarkly is loading or failed to load. This value will override defaultFlagsValues.
   * @returns {{isLoading, value: *}}
   */
  const useFeatureFlag = (flagName, defaultValue = defaultFlagsValues[flagName]) => {
    const ldContext = useContext(LaunchDarklyContext);
    if (!ldContext) {
      return {
        isLoading: false,
        value: defaultValue
      };
    }
    const { isLoading, client } = ldContext;
    const value = isLoading ? defaultValue : client.current.variation(flagName, defaultValue);
    return { isLoading, value };
  };

  const LaunchDarklyConsumer = ({ children, flagName, defaultValue }) => {
    const result = useFeatureFlag(flagName, defaultValue);
    return children(result);
  };

  /**
   *
   * @param flagName
   * @param defaultValue - An optional default value in case launchDarkly is loading or failed to load. This value will override defaultFlagsValues.
   * @returns feature flag's value
   */
  const useFeatureFlagValue = (flagName, defaultValue) => {
    const featureFlag = useFeatureFlag(flagName, defaultValue);
    return featureFlag.value;
  };

  LaunchDarklyConsumer.propTypes = {
    /**
     * FF name.
     */
    flagName: PropTypes.string.isRequired,
    /**
     * An optional default value in case launchDarkly is loading or failed to load. This value will override defaultFlagsValues.
     */
    defaultValue: PropTypes.any,
    /**
     * A function getting ({isLoading, value}) and returns a node.
     */
    children: PropTypes.func.isRequired
  };

  return {
    LaunchDarklyProvider,
    LaunchDarklyConsumer,
    useFeatureFlag,
    useFeatureFlagValue,
    LaunchDarklyContext
  };
};
export const MatrixLaunchDarklyContext = getOrCreateGlobalContext(
  CONTEXT_NAMES.MatrixLaunchDarklyContext
);

export const {
  LaunchDarklyProvider: MatrixLaunchDarklyProvider,
  LaunchDarklyConsumer: MatrixLaunchDarklyConsumer,
  useFeatureFlag: useMatrixFeatureFlag
} = createLaunchDarklyContext({ context: MatrixLaunchDarklyContext });

export default createLaunchDarklyContext;
